import { useEffect, useState } from "react";
import { TTableData } from "../../Simulation";
import TableDestino, { TSelectionState } from "../../Tables/TableDestino";
import TableOrigen from "../../Tables/TableOrigen";
import { Dropdown, PrimaryButton, Spinner } from "@fluentui/react";
import { dropdownStylesSingle } from "../../Styles/DropDownStyles";
import { RatioKeys, TOmniaSimulationResponse } from "../../../../services/OmniaService";
import { PERIODOS_OPTIMIZAR, RATIOS_OPTIMIZAR } from "../../Texts/DropdownData";
import { ErrorModal } from "../../../../components/Modals/ErrorModal";
import { TableData } from "../../../Portfolios/File/Util/TableData";
import { TButtonsTable, TDataWallet, TRatiosCarterasData } from "../../../Portfolios/File/types/CarteraRatioTypes";
import RatioTable from "../../../../components/RatioTable/RatioTable";
import { useBoolean } from "@fluentui/react-hooks";
import CustomTeachingBubble from "../../../../components/CustomTeachingBubble/CustomTeachingBubble";
import { tipImages, tipTexts } from "../../../Portfolios/File/TooltipData/TooltipData";

import HighstockSimulation, { RangeButton } from "../../Component/HighStockSimulation/HighStockSimulation";
import { buttonsTableList } from "../../Texts/texts";

const BASE_CLASS = "backtesting";

type TBacktestingProps = {
    originalFunds: TTableData[];
    nuevosFunds: TTableData[];
    simulationInfo?: TOmniaSimulationResponse;
    tableData: TRatiosCarterasData[];
    ratioPeriod: TRatioAndPeriod;
    searchFunc: () => Promise<void>;
    simulationFunc: (selected: TSelectionState) => void;
    payloadFunc: (optionKey: string, option: string) => void;
    inputsError: boolean;
};

export type TRatioAndPeriod = {
    ratio: RatioKeys;
    period: string;
};

const Backtesting = ({ 
    originalFunds,
    nuevosFunds,
    ratioPeriod,
    simulationInfo,
    tableData,
    inputsError,
    searchFunc,
    payloadFunc,
    simulationFunc
}: TBacktestingProps) => {

    const localTheme = window.localStorage.getItem("theme") || "light";

    const [tooltipRatio, { setTrue: showTooltipRatio, setFalse: hideTooltipRatio }] = useBoolean(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selected, setSelected] = useState<TSelectionState>({} as TSelectionState);
    const [disableSimulation, setDisableSimulation] = useState<boolean>(true);
    const [error, setError] = useState<{status: boolean; message: string; }>({ status: false, message: '' });
    const [tablePeriod, setTablePeriod] = useState<string>('Inception');
    const [resultTable, setResultTable] = useState<TDataWallet[]>([]);
    const [spinData, setSpinData] = useState<boolean>(false);
    const [dataActive, setDataActive] = useState<boolean>(!!simulationInfo);
    const [periodDisabled, setPeriodDisabled] = useState<boolean>(false);

    // Para gestionar tolerancias en caso de necesidad
    const isApproximately100 = (value: number, tolerance = 0.01): boolean => {
        return Math.abs(value - 100) <= tolerance;
    };

    const Simulation = async () => {
        const sumWeights = Object.values(selected).reduce((acc, fund) => acc += fund.data.destinyWeight, 0);
        const isValidWeights = isApproximately100(sumWeights);
        setError({
            status: !isValidWeights,
            message: !isValidWeights? 'El peso total de los nuevos fondos debe ser del 100%.' : ''
        });
 
        if (isValidWeights) simulationFunc(selected);
    };

    const checkPeriodDisabled = (inputData: string) => {
        const disablePeriodInputs = ["pca", "gastos_corrientes", "clase_limpia"];
        setPeriodDisabled(disablePeriodInputs.includes(inputData));
    };

    useEffect(() => {
        setDisableSimulation(!Object.values(selected).some(fund => fund.checked))
    }, [selected])

    useEffect(() => {
        setIsLoading(false);
    }, [nuevosFunds]);

    useEffect(() => {
        setSpinData(true);
        setResultTable(TableData({ tablePeriod: tablePeriod ?? "Inception", ratios: tableData }))
    }, [simulationInfo, tablePeriod]);

    return (
        <div className={BASE_CLASS}>
            {dataActive &&
                <div className={`${BASE_CLASS}__data__container`}>
                    <div id="table_perdiod_id" className={`${BASE_CLASS}__buttons__period`}>
                        {buttonsTableList.map((buttonTable: TButtonsTable) => (
                            <button className={`${tablePeriod === buttonTable.value ? 'active' : ''} fileButtons`} 
                                onClick={() => {
                                    setTablePeriod(buttonTable.value)
                                }}>{buttonTable.text}</button>
                        ))}
                    
                        {/* <img id="tip_location" onMouseEnter={showTooltipRatio} onClick={showTooltipRatio} style={{ verticalAlign: 'bottom' }} src="/icons/informationBlack.svg" alt="Información" /> */}
                    
                        {/* {tooltipRatio && <CustomTeachingBubble texts={tipTexts} images={tipImages} targetIdentifier="tip_location" showTooltip={hideTooltipRatio} />}     */}
                    </div>
                    {!spinData
                        ? <Spinner size={3} style={{ padding: "1em" }} />
                        : <div className={`${BASE_CLASS}__graphs__container`}>
                            <div className={`${BASE_CLASS}__ratiosCard`}>
                                <RatioTable titleCard="Ratios Cartera" ratios={tableData} resultTable={resultTable} isLoading={!spinData} />
                            </div>
                            <div className={`${BASE_CLASS}__graphCard`}>
                                <HighstockSimulation
                                    serieTemporal={
                                        simulationInfo?.resultados.serie_temporal
                                            ? simulationInfo.resultados.serie_temporal
                                            : []
                                        }
                                    period={tablePeriod.toUpperCase() as RangeButton}
                                />
                            </div>
                        </div>}
                </div>
            }          

            <div className={`${BASE_CLASS}__buttons__container`}>
                <div className={`${BASE_CLASS}__buttons__box`}>
                    <Dropdown
                        label="Ratio"
                        placeholder="Ratio"
                        className={`${BASE_CLASS}__dropdown`}
                        options={RATIOS_OPTIMIZAR}
                        onChange={(_, option) => {
                            payloadFunc(option ? option?.key as string : "", "ratio")
                            checkPeriodDisabled(option?.key as string);
                        }
                        }
                        styles={dropdownStylesSingle(localTheme)}
                        calloutProps={{
                            styles: {
                                root: { width: 200 }
                            }
                        }}
                    />
                    <Dropdown
                        label="Período"
                        placeholder="1Y"
                        disabled={periodDisabled}
                        className={`${BASE_CLASS}__dropdown__period`}
                        options={PERIODOS_OPTIMIZAR}
                        onChange={(_, option) => payloadFunc(option ? option.key as string : "", "periodo")}
                        styles={dropdownStylesSingle(localTheme)} />
                    <div className={`${BASE_CLASS}__input__container`}>
                        <span>Flashback</span>
                        <input className={`${BASE_CLASS}__input__flashback`}  placeholder="Semanas" type="number" onChange={event => payloadFunc(event.target.value, "semanasAtras")}></input>
                    </div>
                    <div className={`${BASE_CLASS}__action__buttons__container`}>
                    <PrimaryButton onClick={() => { searchFunc(); setIsLoading(true)}} disabled={!inputsError}>
                        Buscar fondos
                    </PrimaryButton>
                    <PrimaryButton disabled={disableSimulation} onClick={() => {
                        setSpinData(false);
                        setDataActive(true);
                        Simulation();
                         }}>
                        Simular
                    </PrimaryButton>
                    </div>
                </div>

            </div>
            <div className={`${BASE_CLASS}__tables__container`}>
                <TableOrigen data={originalFunds} />
                {isLoading                
                    ? <Spinner size={3} style={{ padding: "1em" }} />
                    : nuevosFunds.length > 0
                        && <TableDestino data={nuevosFunds} ratioAndPeriod={ratioPeriod} onSelectedDestinyFunds={setSelected}
                />}
                        
            </div>
            {error.status &&
                <ErrorModal title={"Error pesos fondos nuevos"}
                    body={
                        <div>
                            <p>{error.message}</p>
                        </div>
                    }
                    btnSubmitText="Aceptar"
                    showModal={error.status}
                    onSubmit={() =>
                    {
                        setError({ status: false, message: "" });
                    }}
                    onClose={() =>
                    {
                        setError({ status: false, message: "" });
                    }
                } />}
        </div>
    )
}

export default Backtesting;